import { InvoiceReceiptTransactionDTO } from "@gymflow/types";
import { useTranslation } from "react-i18next";

import PMCustomAllIcon from "../../../atoms/icons/PMCustomAllIcon";
import PMCustomCashIcon from "../../../atoms/icons/PMCustomCashIcon";
import { PMLinkIcon } from "../../../atoms/icons/PMLinkIcon";
import {
  CARD_BRAND_ICONS,
  PAYMENT_METHOD_TYPE_ICONS,
  WALLET_ICONS,
} from "../../../PaymentMethodIcons";

const directDebitTypes = [
  "DIRECT_DEBIT",
  "UK_DIRECT_DEBIT",
  "AU_DIRECT_DEBIT",
  "US_DIRECT_DEBIT",
  "CA_DIRECT_DEBIT",
  "EU_DIRECT_DEBIT",
  "UNSUPPORTED",
];

const PaymentMethod = ({
  reportItem,
}: {
  reportItem: InvoiceReceiptTransactionDTO;
}) => {
  const { t } = useTranslation();

  if (reportItem.amount === 0) {
    return (
      <div className="flex items-center gap-x-2 text-sm font-semibold text-gray-950">
        {t("portal.common.none")}
      </div>
    );
  }

  const handlers: Record<
    string,
    (item: typeof reportItem) => { icon: React.ReactNode; text: string }
  > = {
    CUSTOM: (item) => ({
      icon:
        item.customPaymentCategoryName === "Cash" ? (
          <PMCustomCashIcon />
        ) : (
          <PMCustomAllIcon />
        ),
      text: item.customPaymentCategoryName ?? "",
    }),
    CARD: (item) => ({
      icon: item.cardWalletType
        ? WALLET_ICONS[item.cardWalletType]
        : item.cardBrand
          ? CARD_BRAND_ICONS[item.cardBrand]
          : CARD_BRAND_ICONS.unknown,
      text: t("page.reports.revenueTotal.table.paymentMethodColumn.endingIn", {
        last4Digits: item.last4Digits,
      }),
    }),
    DIRECT_DEBIT: (item) => ({
      icon: item.paymentMethodType
        ? PAYMENT_METHOD_TYPE_ICONS[item.paymentMethodType]
        : PAYMENT_METHOD_TYPE_ICONS.UNSUPPORTED,
      text: t("page.reports.revenueTotal.table.paymentMethodColumn.endingIn", {
        last4Digits: item.last4Digits,
      }),
    }),
    LINK: (item) => ({
      icon: <PMLinkIcon />,
      text: item.linkEmail ?? "",
    }),
    WRITE_OFF: () => ({
      icon: undefined,
      text: t("page.reports.revenueTotal.table.paymentMethodColumn.writeOff"),
    }),
    REFUND_DEBIT: () => ({
      icon: undefined,
      text: t("page.reports.revenueTotal.table.paymentMethodColumn.refund"),
    }),
    CREDIT: (item) => ({
      icon:
        item.customPaymentCategoryName === "Cash" ? (
          <PMCustomCashIcon />
        ) : (
          <PMCustomAllIcon />
        ),
      text: item.customPaymentCategoryName ?? "",
    }),
    MANUAL_CREDIT: () => ({
      icon: undefined,
      text: t("page.reports.revenueTotal.table.paymentMethodColumn.credit"),
    }),
    WALLET_CREDIT: () => ({
      icon: undefined,
      text: t(
        "page.reports.revenueTotal.table.paymentMethodColumn.walletCredit",
      ),
    }),
  };

  const methodType = directDebitTypes.includes(
    reportItem.paymentMethodType || "",
  )
    ? "DIRECT_DEBIT"
    : reportItem.paymentMethodType || reportItem.type || "CUSTOM";

  const handler = handlers[methodType];

  const { icon, text } = handler
    ? handler(reportItem)
    : {
        icon: <PMCustomAllIcon />,
        text:
          reportItem.paymentMethodType ??
          t("components.paymentsSidebarProvider.invoice.unknown"),
      };

  return (
    <div className="flex items-center gap-x-2 text-sm font-semibold text-gray-950">
      {icon}
      {text}
    </div>
  );
};

export default PaymentMethod;
